import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, FormHelperText, Typography } from "@mui/material";

const DIGIT_COUNT = 6;

const sanitizeCode = (value) => (value || "").replace(/\D/g, "").slice(0, DIGIT_COUNT);

const visuallyHiddenInputSx = {
  position: "absolute",
  opacity: 0,
  width: 1,
  height: 1,
  pointerEvents: "none",
};

const SixDigitCodeInput = ({
  value,
  onChange,
  label = "6-Digit Code",
  disabled = false,
  error = false,
  helperText,
  autoFocus = false,
  sx,
}) => {
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const normalizedValue = sanitizeCode(value);

  useEffect(() => {
    if (autoFocus && inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [autoFocus, disabled]);

  const focusInput = () => {
    if (!disabled && inputRef.current) inputRef.current.focus();
  };

  return (
    <Box sx={sx}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block", textAlign: "center" }}>
        {label}
      </Typography>

      <Box onClick={focusInput} sx={{ position: "relative", cursor: disabled ? "default" : "text" }}>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={DIGIT_COUNT}
          value={normalizedValue}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(sanitizeCode(e.target.value))}
          style={visuallyHiddenInputSx}
          aria-label={label}
        />

        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          {Array.from({ length: DIGIT_COUNT }).map((_, index) => {
            const char = normalizedValue[index] || "";
            const activeSlot = focused && (index === normalizedValue.length || (normalizedValue.length === DIGIT_COUNT && index === DIGIT_COUNT - 1));
            return (
              <Box
                key={index}
                sx={{
                  width: 42,
                  height: 52,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: error ? "error.main" : activeSlot ? "primary.main" : "divider",
                  bgcolor: disabled ? "action.disabledBackground" : "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1,
                  fontFamily: "monospace",
                  color: char ? "text.primary" : "text.disabled",
                }}
              >
                {char || "_"}
              </Box>
            );
          })}
        </Box>
      </Box>

      {(helperText || error) && (
        <FormHelperText error={!!error} sx={{ textAlign: "center", mt: 1 }}>
          {helperText || " "}
        </FormHelperText>
      )}
    </Box>
  );
};

SixDigitCodeInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  autoFocus: PropTypes.bool,
  sx: PropTypes.object,
};

export default SixDigitCodeInput;
